import { HotelLastSecondHotelPage } from "@website/accommodation-ui/hotel-last-second";
import { HotelReserveRoomsFilter } from "@website/accommodation-ui/hotel-reserve-rooms-filter";
import { HotelReserveCard } from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-card";
import { HotelReserveCardDetail } from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-card/hotel-reserve-card-detail";
import { HotelReserveEmptyResult } from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-empty-result";
import {
  HotelReserveRoomsLayoutPropertiesType,
  HotelReservedCardType
} from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-room-layout.types";
import { HotelSearch } from "@website/accommodation-ui/hotel-search";
import InstallmentStatus from "@website/accommodation-ui/installment-status/installment-status";
import { useIsVisible } from "@website/hooks/is-visible";
import { MaterialIcon } from "@website/icons/material";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { addEventToTracking } from "@website/tracking/user";
import { Button } from "@website/ui/button";
import { Loading } from "@website/ui/loading";
import { getChannel } from "@website/utils/channel/get-channel";
import { deepCompare } from "@website/utils/deep-compare";
import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";
import { useRouter } from "next/router";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { HotelReserveCardSkeleton } from "./hotel-reserve-card-skeleton";
const Component = (properties: HotelReserveRoomsLayoutPropertiesType) => {
  const {
    cancellation_policy,
    hotelId,
    hotelRooms,
    searchProperties,
    onClick,
    loading,
    hasMorContentButton,
    hotelType,
    isHotelActive,
    roomDetail,
    onInView,
    snappPayData,
    roomsFilter
  } = properties;
  const router = useRouter();
  const [isShowUnavalables, setIsShowUnavalables] = useState(true);

  const roomsFilterObject = Object.fromEntries(
    Object.entries(router?.query)
      .filter((value) => value?.[0]?.startsWith("roomFilter_"))
      .map((item) => [item?.[0]?.replace("roomFilter_", ""), item?.[1]])
  );

  const filteredHotelRooms = useMemo(
    () =>
      isEmpty(roomsFilter)
        ? hotelRooms
        : hotelRooms?.filter(
            (room) =>
              Object.entries(roomsFilterObject)?.every(([key, value]) =>
                key === "roomType"
                  ? String(value)
                      ?.split("_")
                      ?.some((item) => room?.roomTypeTitle === item)
                  : key === "withDiscount"
                    ? room?.price !== room?.priceOff
                    : key === "availableRooms"
                      ? [key, value]
                      : Boolean(room?.[key]) === JSON?.parse(String(value))
              )
          ),
    [hotelRooms, roomsFilter, roomsFilterObject]
  );

  const lastSecondRooms = filteredHotelRooms?.filter(
    (room) =>
      isHotelActive &&
      (room?.availableRooms || room?.rackIncluded) &&
      room?.regularBadges?.some?.((badge) => badge.type === "last_minute")
  );

  const availableRooms = filteredHotelRooms?.filter(
    (room) =>
      isHotelActive &&
      (room?.availableRooms || room?.rackIncluded) &&
      !room?.regularBadges?.some?.((badge) => badge.type === "last_minute")
  );

  const unavailableRooms = filteredHotelRooms?.filter(
    (room) => !isHotelActive || (!room?.availableRooms && !room?.rackIncluded)
  );

  useEffect(() => {
    setIsShowUnavalables(
      unavailableRooms?.length > 0 &&
        filteredHotelRooms?.length !== 0 &&
        (lastSecondRooms?.length === 0 || availableRooms?.length === 0) &&
        get(router?.query, "roomFilter_availableRooms") !== "availableRooms"
    );
  }, [
    availableRooms?.length,
    filteredHotelRooms?.length,
    lastSecondRooms?.length,
    router?.query,
    unavailableRooms?.length
  ]);

  const { reference, inView } = useIsVisible({
    rootMargin: "-100px"
  });
  const goToRoom = useCallback(() => {
    addEventToTracking(USER_TRACKING_EVENTS.FOOTER_CTA_CLICK, {
      product: "dom-hotel"
    });
    if (!inView)
      reference?.current?.scrollIntoView?.({
        behavior: "smooth"
      });
  }, []);

  useEffect(() => {
    if (inView && process.env.NEXT_PUBLIC_APP_NAME === "hotel-domestic")
      addEventToTracking(USER_TRACKING_EVENTS.SCROLL_TO_ROOM_SECTION, {
        product: "dom-hotel"
      });
  }, [inView]);

  return (
    <div
      className="bg-dim-background mx-[-24px] flex min-h-[304px] flex-col xl:mx-0 xl:min-h-[264px] xl:max-w-[1200px] xl:rounded-2xl"
      id="hotel_rooms"
      ref={reference}
    >
      {!inView && process.env.NEXT_PUBLIC_APP_NAME === "hotel-domestic" && (
        <Button
          StartIcon={
            <MaterialIcon
              name={`${
                reference?.current &&
                reference?.current?.getBoundingClientRect()?.y > 0
                  ? "arrow_downward"
                  : "arrow_upward"
              }`}
            />
          }
          color="secondary"
          variant="round"
          text="مشاهده اتاق ها و رزرو"
          size="large"
          onClick={goToRoom}
          className="!text-body-2 shadow-3-dark !fixed inset-x-0 bottom-6 z-20 mx-auto w-[192px]"
        />
      )}
      {getChannel() !== "ads" && (
        <div className="px-5 pt-5">
          <InstallmentStatus isEligible={Boolean(snappPayData?.isEligible)} />
        </div>
      )}
      <div className="p-6 pb-0 xl:p-12 xl:pb-0">
        <h2 className="text-subtitle-1 md:text-headline-6 pb-2">اتاق ها</h2>
        <div className="text-on-surface-medium-emphasis text-caption pb-4">
          پیشنهاد بر اساس جستجو و فیلترهای انتخابی شما
        </div>
        <div>
          {isHotelActive === true && loading ? (
            <div className="flex justify-center">
              <Loading color="primary" size="lg" />
            </div>
          ) : (
            <HotelSearch
              {...searchProperties}
              searchType="hotel"
              isFloatCloseButton={false}
            />
          )}
        </div>
        <HotelReserveRoomsFilter roomsFilter={roomsFilter} />
      </div>
      {lastSecondRooms?.length ? (
        <HotelLastSecondHotelPage
          hasMorContentButton={hasMorContentButton}
          lastSecondRooms={lastSecondRooms}
          onInView={onInView}
          roomDetail={roomDetail}
          snappPayData={snappPayData}
        />
      ) : null}
      <div className="p-6 pt-0 xl:p-12 xl:pt-0">
        {availableRooms?.length === 0 &&
          !isShowUnavalables &&
          lastSecondRooms?.length === 0 &&
          hotelRooms?.length > 0 && <HotelReserveEmptyResult />}
        {((!loading && !hotelRooms?.length) || isHotelActive === false) && (
          <div className="pt-6 lg:pt-[120px] xl:pt-[80px]">
            <div className="flex flex-col gap-6">
              <span className="block text-center">
                {isHotelActive === false
                  ? "هتل مورد نظر غیرفعال است!"
                  : "اتاقی یافت نشد، لطفا تاریخ دیگری را جستجو کنید!"}
              </span>
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex flex-col gap-6 pt-6 lg:pt-[120px] xl:pt-[80px]">
            <HotelReserveCardSkeleton />
            <HotelReserveCardSkeleton />
            <HotelReserveCardSkeleton />
          </div>
        ) : (
          (availableRooms?.length ?? 0) > 0 && (
            <div className="pt-6 lg:pt-[120px] xl:pt-[80px]">
              <div className="text-subtitle-1 md:text-headline-6 pb-4">
                اتاق های دارای ظرفیت
              </div>
              <div className="flex flex-col gap-6">
                {availableRooms.map((room) => (
                  <HotelReserveCard
                    key={room.id}
                    onClick={onClick}
                    {...room}
                    hasMorContentButton={hasMorContentButton}
                    hotelType={hotelType}
                    onInView={(inView) => onInView?.(inView, room)}
                    roomDetail={roomDetail}
                    snappPayData={snappPayData}
                  />
                ))}
              </div>
            </div>
          )
        )}
        {isShowUnavalables && unavailableRooms.length > 0 && (
          <div className="pt-[56px]">
            <div className="text-subtitle-1 md:text-headline-6 pb-4">
              اتاق های تکمیل ظرفیت
            </div>
            <div className="flex flex-col gap-6">
              {unavailableRooms.map((room) => (
                <HotelReserveCard
                  key={room.id}
                  {...room}
                  hasMorContentButton={hasMorContentButton}
                  hotelType={hotelType}
                  onInView={(inView) => onInView?.(inView, room)}
                  roomDetail={roomDetail}
                  snappPayData={snappPayData}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <HotelReserveCardDetail
        {...((lastSecondRooms.length > 0
          ? lastSecondRooms
          : availableRooms
        )?.find(
          (room) => room.id === roomDetail?.roomId
        ) as HotelReservedCardType)}
        roomDetail={roomDetail}
        hotelId={hotelId}
        cancellation_policy={cancellation_policy}
      />
    </div>
  );
};

export const HotelReserveRoomsLayout = memo(Component, deepCompare);
